/**
 * Live design-mockup preview. Composites the customer's uploaded artwork
 * (or a design image passed through from the "get it pressed" flow) onto
 * a photo of the product, entirely client-side (no upload, no network
 * call besides loading images) - see design-mockup-canvas.liquid for the
 * markup this reads.
 *
 * 'garment' pattern: draws a dashed print-area box, lets the customer drag
 * the design and resize it with a slider, clamped so it can't leave the
 * garment (and can't creep above the print area's top edge, which is set
 * with at least a 2-inch collar clearance - a real production constraint,
 * not just a look). 'flat' pattern: the design just fills the whole
 * canvas, fitted and centered, no interaction (Stickers/Cards - the whole
 * item is the print, so there's no placement decision to make).
 *
 * Color swap: when the product has per-color photography (see the
 * `.design-mockup__color-images` JSON block emitted by
 * design-mockup-canvas.liquid), this listens to the theme's own color
 * swatch radio inputs and swaps the base photo to match - falls back to
 * a single default photo for products without per-color photography yet.
 *
 * Front/Back toggle: shown only when the current color has a "back" photo
 * available. Switching sides swaps the base photo and re-fits whichever
 * design was uploaded for that side (position doesn't carry over between
 * sides - front and back are different physical print jobs). On
 * products with a Design Option (see custom-artwork-upload.liquid),
 * front and back are two separate uploads and each previews only on its
 * own side; on products without one, the single generic upload previews
 * on both sides, same as before that split existed. Also listens to the
 * product's own "Design Option" radio inputs (when present) and
 * auto-switches to the back photo whenever "Back Only" is selected,
 * since that's the only side actually getting printed - and back to
 * front for every other Design Option value.
 *
 * Touch scrolling: only the small `.design-mockup__hit-area` overlay -
 * positioned and sized each redraw to exactly match the design's current
 * on-screen box - has touch-action:none and the drag listeners. The
 * canvas itself keeps the browser's default touch-action, so a shopper's
 * finger scrolls the page normally anywhere on the mockup except directly
 * on their uploaded design. (Previously touch-action:none sat on the
 * whole canvas, which is most of the screen on mobile - that made the
 * page nearly unscrollable wherever the mockup appeared.)
 *
 * This is a preview layer only. The file that actually gets pressed is
 * still whatever the paired file input (see custom-artwork-upload.liquid)
 * submits to /cart/add.js - this script never touches that value.
 */
window.DesignMockup = (function () {
  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function parseColorImages(root) {
    var script = root.querySelector('.design-mockup__color-images');
    if (!script) return {};
    try {
      var data = JSON.parse(script.textContent);
      return data && typeof data === 'object' ? data : {};
    } catch (e) {
      return {};
    }
  }

  function initRoot(root) {
    var canvas = root.querySelector('.design-mockup__canvas');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');
    var pattern = root.dataset.pattern;
    var isGarment = pattern === 'garment';

    var area = isGarment
      ? {
          x: toNumber(root.dataset.areaX, 0),
          y: toNumber(root.dataset.areaY, 0),
          w: toNumber(root.dataset.areaW, 1),
          h: toNumber(root.dataset.areaH, 1),
        }
      : { x: 0, y: 0, w: 1, h: 1 };

    var resizeSlider = root.querySelector('.design-mockup__resize');
    var emptyHint = root.querySelector('.design-mockup__empty-hint');
    var hitArea = root.querySelector('.design-mockup__hit-area');
    var sideToggle = root.querySelector('.design-mockup__side-toggle');
    var frontButton = root.querySelector('[data-mockup-side="front"]');
    var backButton = root.querySelector('[data-mockup-side="back"]');

    var colorImages = parseColorImages(root);
    var colorPosition = root.dataset.colorOptionPosition;
    // Read the actually-checked swatch directly rather than trusting
    // data-initial-color from Liquid - server-side variant resolution
    // doesn't reliably match whichever swatch the theme's own JS shows
    // checked on load (confirmed empty in testing), so the live DOM state
    // is the more trustworthy source.
    var currentColor = root.dataset.initialColor || '';
    if (colorPosition) {
      var checkedColorInput = document.querySelector('input[name="option' + colorPosition + '"]:checked');
      if (checkedColorInput) currentColor = checkedColorInput.value;
    }

    // A "Back Only" Design Option means the back is the only side actually
    // getting printed, so the preview should open on the back photo rather
    // than showing the front and leaving the shopper to find the toggle.
    var designOptionPosition = root.dataset.designOptionPosition;
    var currentSide = 'front';
    if (designOptionPosition) {
      var checkedDesignOptionInput = document.querySelector('input[name="option' + designOptionPosition + '"]:checked');
      if (checkedDesignOptionInput && checkedDesignOptionInput.value === 'Back Only') {
        currentSide = 'back';
      }
    }

    var baseImage = new Image();
    // Separate images per side - a customer can legitimately want a
    // different design on the back than the front, so the same upload
    // can't just be mirrored onto both (see custom-artwork-upload.liquid
    // for the front/back/sleeve upload split this mirrors).
    var designImages = { front: null, back: null };

    var state = {
      scale: 1,
      minScale: 1,
      maxScale: 1,
      offsetX: 0,
      offsetY: 0,
    };

    var dragging = false;
    var lastPointerX = 0;
    var lastPointerY = 0;

    function currentEntry() {
      return colorImages[currentColor];
    }

    function currentDesignImage() {
      return designImages[currentSide] || null;
    }

    function updateEmptyHint() {
      if (emptyHint) emptyHint.hidden = !!currentDesignImage();
    }

    function resolveBaseImageUrl() {
      var entry = currentEntry();
      if (entry && entry[currentSide]) return entry[currentSide];
      if (entry && entry.front) return entry.front;
      return root.dataset.baseImage;
    }

    function updateSideToggleVisibility() {
      if (!sideToggle) return;
      var entry = currentEntry();
      var hasBack = !!(entry && entry.back);
      sideToggle.hidden = !hasBack;
      if (!hasBack) currentSide = 'front';
      if (frontButton) frontButton.setAttribute('aria-pressed', String(currentSide === 'front'));
      if (backButton) backButton.setAttribute('aria-pressed', String(currentSide === 'back'));
    }

    function loadBaseImage() {
      var url = resolveBaseImageUrl();
      if (!url || url === baseImage.src) return;
      baseImage.src = url;
    }

    // Single source of truth for the design's current box, in canvas-
    // internal pixel coordinates - both draw() (to render it) and
    // updateHitArea() (to position the draggable overlay over it) read
    // from here, so the overlay can never drift out of sync with what's
    // actually drawn.
    function designGeometryPx() {
      var designImage = currentDesignImage();
      if (!designImage || !designImage.complete || !designImage.naturalWidth) return null;
      var areaPxW = area.w * canvas.width;
      var areaPxH = area.h * canvas.height;
      var areaCx = area.x * canvas.width + areaPxW / 2;
      var areaCy = area.y * canvas.height + areaPxH / 2;
      var drawW = designImage.naturalWidth * state.scale;
      var drawH = designImage.naturalHeight * state.scale;
      return {
        cx: areaCx + state.offsetX,
        cy: areaCy + state.offsetY,
        w: drawW,
        h: drawH,
      };
    }

    // Positions the small touch-action:none overlay directly over the
    // design's current on-screen box, so only that patch of the canvas
    // blocks scrolling - everywhere else the shopper's finger scrolls the
    // page like normal. Hidden (and un-hit-testable) whenever there's no
    // design to drag.
    function updateHitArea() {
      if (!hitArea) return;
      var box = isGarment ? designGeometryPx() : null;
      if (!box) {
        hitArea.hidden = true;
        return;
      }
      var scale = canvas.getBoundingClientRect().width / canvas.width;
      hitArea.style.left = (box.cx - box.w / 2) * scale + 'px';
      hitArea.style.top = (box.cy - box.h / 2) * scale + 'px';
      hitArea.style.width = box.w * scale + 'px';
      hitArea.style.height = box.h * scale + 'px';
      hitArea.hidden = false;
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (baseImage.complete && baseImage.naturalWidth) {
        ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);
      }

      if (isGarment) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.strokeRect(
          area.x * canvas.width,
          area.y * canvas.height,
          area.w * canvas.width,
          area.h * canvas.height
        );
        ctx.restore();
      }

      var box = designGeometryPx();
      if (box) {
        ctx.drawImage(currentDesignImage(), box.cx - box.w / 2, box.cy - box.h / 2, box.w, box.h);
      }

      updateHitArea();
    }

    function fitDesignToArea() {
      updateEmptyHint();
      var designImage = currentDesignImage();
      if (!designImage) {
        draw();
        return;
      }
      var areaPxW = area.w * canvas.width;
      var areaPxH = area.h * canvas.height;
      var fitScale = Math.min(
        areaPxW / designImage.naturalWidth,
        areaPxH / designImage.naturalHeight
      );
      state.minScale = fitScale;
      state.maxScale = fitScale * (isGarment ? 1.8 : 1);
      state.scale = fitScale;
      state.offsetX = 0;
      state.offsetY = 0;
      if (resizeSlider) resizeSlider.value = 0;
      draw();
    }

    function clampOffset() {
      var designImage = currentDesignImage();
      if (!designImage || !isGarment) return;
      var areaPxW = area.w * canvas.width;
      var areaPxH = area.h * canvas.height;
      var drawW = designImage.naturalWidth * state.scale;
      var drawH = designImage.naturalHeight * state.scale;
      // Keep at least ~70% of the design's box overlapping the print area
      // rather than requiring full containment - lets an intentionally
      // off-center design happen, but not a fully-off-garment one. The
      // print area's own top edge already carries the 2-inch collar
      // clearance, so holding the design inside this bound keeps it below
      // that line rather than letting it creep back up toward the collar.
      var boundX = areaPxW / 2 + drawW * 0.35;
      var boundY = areaPxH / 2 + drawH * 0.35;
      state.offsetX = Math.max(-boundX, Math.min(boundX, state.offsetX));
      state.offsetY = Math.max(-boundY, Math.min(boundY, state.offsetY));
    }

    function loadDesignForSide(side, src) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        designImages[side] = img;
        if (side === currentSide) fitDesignToArea();
      };
      img.src = src;
    }

    function loadFileForSide(side, file) {
      if (!file || file.type.indexOf('image/') !== 0) return;
      var reader = new FileReader();
      reader.onload = function (event) {
        loadDesignForSide(side, event.target.result);
      };
      reader.readAsDataURL(file);
    }

    function canvasPoint(clientX, clientY) {
      var rect = canvas.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / rect.width) * canvas.width,
        y: ((clientY - rect.top) / rect.height) * canvas.height,
      };
    }

    function onPointerDown(event) {
      if (!currentDesignImage()) return;
      dragging = true;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      hitArea.setPointerCapture(event.pointerId);
    }

    function onPointerMove(event) {
      if (!dragging) return;
      var scaleFactor = canvas.width / canvas.getBoundingClientRect().width;
      state.offsetX += (event.clientX - lastPointerX) * scaleFactor;
      state.offsetY += (event.clientY - lastPointerY) * scaleFactor;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      clampOffset();
      draw();
    }

    function onPointerUp(event) {
      dragging = false;
      if (hitArea.hasPointerCapture && hitArea.hasPointerCapture(event.pointerId)) {
        hitArea.releasePointerCapture(event.pointerId);
      }
    }

    // The base photo's own aspect ratio drives the canvas - these are
    // portrait product photos, not square, so forcing a square crop would
    // visibly stretch/squish the garment. A fixed internal width with a
    // height computed from the loaded image keeps compositing math in
    // 0-1 fractional area coordinates working unchanged.
    baseImage.crossOrigin = 'anonymous';
    baseImage.onload = function () {
      var targetWidth = 1000;
      canvas.width = targetWidth;
      canvas.height = Math.round(targetWidth * (baseImage.naturalHeight / baseImage.naturalWidth));
      draw();
    };
    loadBaseImage();

    // Products with a Design Option get separate front/back upload
    // fields (see custom-artwork-upload.liquid) - each previews only on
    // its own side. Products without one keep a single generic upload
    // that previews on both sides, same as before that split existed.
    var frontUploadInput = root.dataset.uploadInputFront
      ? document.getElementById(root.dataset.uploadInputFront)
      : null;
    var backUploadInput = root.dataset.uploadInputBack
      ? document.getElementById(root.dataset.uploadInputBack)
      : null;

    if (frontUploadInput) {
      frontUploadInput.addEventListener('change', function () {
        if (frontUploadInput.files && frontUploadInput.files[0]) {
          loadFileForSide('front', frontUploadInput.files[0]);
        }
      });
      if (backUploadInput) {
        backUploadInput.addEventListener('change', function () {
          if (backUploadInput.files && backUploadInput.files[0]) {
            loadFileForSide('back', backUploadInput.files[0]);
          }
        });
      }
    } else {
      var legacyUploadInput = root.dataset.uploadInput
        ? document.getElementById(root.dataset.uploadInput)
        : null;
      if (legacyUploadInput) {
        legacyUploadInput.addEventListener('change', function () {
          if (legacyUploadInput.files && legacyUploadInput.files[0]) {
            loadFileForSide('front', legacyUploadInput.files[0]);
            loadFileForSide('back', legacyUploadInput.files[0]);
          }
        });
      }
    }

    // Watch the theme's own color-swatch radio inputs (product-form.js
    // owns selection state; this only listens, never drives it) and swap
    // the base photo when the shopper picks a different color.
    if (colorPosition && Object.keys(colorImages).length) {
      var colorInputs = document.querySelectorAll('input[name="option' + colorPosition + '"]');
      colorInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          currentColor = input.value;
          updateSideToggleVisibility();
          loadBaseImage();
        });
      });
    }

    // Watch the Design Option radio inputs (product-form.js owns selection
    // state; this only listens) and jump the preview to match: "Back Only"
    // switches to the back photo since that's the only side being printed,
    // anything else switches back to the front.
    if (designOptionPosition) {
      var designOptionInputs = document.querySelectorAll('input[name="option' + designOptionPosition + '"]');
      designOptionInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          currentSide = input.value === 'Back Only' ? 'back' : 'front';
          updateSideToggleVisibility();
          loadBaseImage();
          fitDesignToArea();
        });
      });
    }

    if (sideToggle) {
      if (frontButton) {
        frontButton.addEventListener('click', function () {
          currentSide = 'front';
          updateSideToggleVisibility();
          loadBaseImage();
          fitDesignToArea();
        });
      }
      if (backButton) {
        backButton.addEventListener('click', function () {
          currentSide = 'back';
          updateSideToggleVisibility();
          loadBaseImage();
          fitDesignToArea();
        });
      }
    }
    updateSideToggleVisibility();

    if (isGarment) {
      if (hitArea) {
        hitArea.addEventListener('pointerdown', onPointerDown);
        hitArea.addEventListener('pointermove', onPointerMove);
        hitArea.addEventListener('pointerup', onPointerUp);
        hitArea.addEventListener('pointercancel', onPointerUp);
      }
      // Keeps the overlay aligned with the design's on-screen position
      // after an orientation change or a browser chrome resize (e.g.
      // Safari's address bar collapsing on scroll).
      window.addEventListener('resize', updateHitArea);

      if (resizeSlider) {
        resizeSlider.addEventListener('input', function () {
          if (!currentDesignImage()) return;
          var t = toNumber(resizeSlider.value, 0) / 100;
          state.scale = state.minScale + (state.maxScale - state.minScale) * t;
          clampOffset();
          draw();
        });
      }
    }

    draw();

    // The "get it pressed" pass-through flow (press-this-design-cta.liquid
    // -> design-summary-from-query.js) carries the picked design's photo as
    // ?design_image= on the URL. Read it directly here rather than relying
    // on script-load-order coordination with that other script - two
    // separate DOMContentLoaded listeners firing in a specific order is
    // fragile and was the cause of a real bug (design_image arriving too
    // late to be read).
    var passthroughImage = new URLSearchParams(window.location.search).get('design_image');
    if (passthroughImage) {
      loadDesignForSide('front', passthroughImage);
      loadDesignForSide('back', passthroughImage);
    }
  }

  function init(root) {
    if (!root) return;
    initRoot(root);
  }

  return { init: init };
})();
